import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { EMPTY, Observable, of, switchMap, take } from "rxjs";
import { environment } from "src/environments/environment";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { ResolverHelper } from "./resolver.helper";
import { SearchMode, SearchStateService } from "src/services/search-state.service";

export const OwnerSearchResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> => {
    const queryParams = route.queryParams;
    const hasName = queryParams.hasOwnProperty("name");
    const hasIdentity = queryParams.hasOwnProperty("identification");
    // The owner link on the infopage in 4.0 searches on one person
    const evPersonId = Number(queryParams["evPersonId"]);
    const hasEvPersonId = Number.isInteger(evPersonId) && evPersonId > 0;
    const authService = inject(AuthService);
    const searchStateService = inject(SearchStateService);
    const router = inject(Router);

    if (!(hasIdentity || hasName || hasEvPersonId)) {
        return ResolverHelper.redirectToFrontPage(authService, router);
    }

    const name: string = queryParams["name"] ?? "";
    const identity: string = queryParams["identification"] ?? "";
    const place: string = queryParams["place"] ?? "";
    const indirectOwners: boolean = queryParams["indirectOwners"] === "true";

    return ResolverHelper.hasPermission(authService, [EVServiceType.EvWeb_OwnerSearch]).pipe(
        take(1),
        switchMap((hasOwnerSearchRole) => {
            if (!hasOwnerSearchRole) {
                return ResolverHelper.redirectToFrontPage(authService, router);
            }
            return ResolverHelper.hasPermission(authService, [EVServiceType.Internal_Debug]).pipe(
                take(1),
                switchMap((hasDebugRole) => {
                    if (hasDebugRole) {
                        // The searchpage reads the owner search from SearchStateService, so no personal data is kept in the url
                        searchStateService.setSearchPersonIdentificationString(identity || null);
                        searchStateService.setSearchPersonNameString(name || null);
                        searchStateService.setSearchPersonPlaceString(place || null);
                        searchStateService.setSearchIncludeIndirectOwners(indirectOwners);
                        searchStateService.setSearchPersonEvPersonId(hasEvPersonId ? evPersonId : null);
                        searchStateService.setActiveSearchMode(SearchMode.OWNERSEARCH);
                        router.navigate(["/searchpage"], {
                            queryParams: {
                                searchMode: SearchMode.OWNERSEARCH,
                            },
                        });
                    } else if (!(hasIdentity || hasName)) {
                        // The searchpage in 4.0 can not search on evPersonId from the url
                        return ResolverHelper.redirectToFrontPage(authService, router);
                    } else {
                        const url =
                            environment.evwebUrl +
                            "/searchpage?name=" +
                            encodeURIComponent(name) +
                            "&identification=" +
                            encodeURIComponent(identity) +
                            "&place=" +
                            encodeURIComponent(place) +
                            "&indirectOwners=" +
                            encodeURIComponent(indirectOwners.toString()) +
                            "&dosearch=true&searchMode=1";
                        window.open(url, "_self");
                    }
                    return EMPTY;
                }),
            );
        }),
    );
};
